import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
    Box, Flex, Text, Button, Input, InputGroup, InputLeftElement, InputRightElement,
    useToast, Badge, Textarea, IconButton, SimpleGrid, Select, Icon, Skeleton,
    Tooltip, AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader,
    AlertDialogBody, AlertDialogFooter, useDisclosure, Divider,
} from "@chakra-ui/react";
import {
    Search, ChevronLeft, X, ArrowUpDown, Filter, Package, MapPin, Clock,
    ArrowRightLeft, Check, Plus, Minus, ShieldCheck, ScanLine, History,
    MapPinPlus, Info, AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
    getTransferenciaEstoquePosicoes,
    getTransferenciaEstoqueItens,
    salvarTransferenciaEstoque,
    getTransferenciaEstoqueHistorico,
} from "../services/api";
import ProdutoImagem from "../components/ProdutoImagem";
import BarcodeScanner from "../components/BarcodeScanner";
import ScrollToTop from "../components/ScrollToTop";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

const ACCENT = "#F68633";
const FOCUS_RING = "0 0 0 3px rgba(246,134,51,0.18)";

const SAUDE_FAIXAS = {
    boa: { label: "Boa", cor: "#16A34A", bg: "green.50", texto: "green.700" },
    atencao: { label: "Atenção", cor: "#CA8A04", bg: "yellow.50", texto: "yellow.700" },
    critica: { label: "Crítica", cor: "#DC2626", bg: "red.50", texto: "red.700" },
    nunca: { label: "Nunca revisada", cor: "#9CA3AF", bg: "gray.100", texto: "gray.600" },
};

const faixaDe = (p) => SAUDE_FAIXAS[p?.faixa] || SAUDE_FAIXAS.nunca;

const textoSaude = (p) => {
    if (p?.saude === null || p?.saude === undefined) return "Nunca revisada";
    if (p.dias_desde_revisao === 0) return "Revisada hoje";
    if (p.dias_desde_revisao === 1) return "Revisada ontem";
    return `Revisada há ${p.dias_desde_revisao} dias`;
};

const nomeDe = (i) => i?.nome_produto || `Produto ${i?.produto_id || ""}`;

const chavePosicao = (texto) => String(texto || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

const ModoDestino = ({ valor, onMudar }) => (
    <Flex bg="gray.100" p="3px" borderRadius="lg" mb={4} w={{ base: "full", sm: "fit-content" }}>
        {[
            { chave: "existente", rotulo: "Posição existente", icone: MapPin },
            { chave: "nova", rotulo: "Nova posição", icone: MapPinPlus },
        ].map((op) => {
            const ativo = valor === op.chave;
            return (
                <Button key={op.chave} flex={{ base: 1, sm: "0 0 auto" }} h={{ base: "40px", sm: "36px" }} px={4}
                    size="sm" borderRadius="md" fontWeight="semibold" fontSize="sm"
                    bg={ativo ? "white" : "transparent"} color={ativo ? "gray.800" : "gray.500"}
                    boxShadow={ativo ? "0 1px 3px rgba(0,0,0,0.1)" : "none"}
                    _hover={{ bg: ativo ? "white" : "gray.200", color: "gray.800" }}
                    _focusVisible={{ boxShadow: FOCUS_RING }}
                    leftIcon={<Icon as={op.icone} boxSize={4} color={ativo ? ACCENT : "gray.400"} />}
                    aria-pressed={ativo}
                    onClick={() => onMudar(op.chave)}>
                    {op.rotulo}
                </Button>
            );
        })}
    </Flex>
);

const AVISO_DESTINO = {
    nova: { bg: "blue.50", cor: "blue.700", icone: MapPinPlus },
    existente: { bg: "orange.50", cor: "orange.800", icone: Info },
    origem: { bg: "red.50", cor: "red.700", icone: AlertCircle },
};

const PASSOS = ["Origem", "Itens", "Destino", "Confirmar"];

const Stepper = ({ atual }) => (
    <Flex align="center" justify="center" gap={1} mb={5} wrap="wrap">
        {PASSOS.map((nome, idx) => {
            const numero = idx + 1;
            const feito = numero < atual;
            const ativo = numero === atual;
            return (
                <Flex key={nome} align="center" gap={1}>
                    <Flex direction="column" align="center" gap={0.5}>
                        <Flex w="26px" h="26px" borderRadius="full" align="center" justify="center"
                            border="2px solid"
                            borderColor={ativo || feito ? ACCENT : "gray.200"}
                            bg={feito ? ACCENT : "white"}
                            color={feito ? "white" : ativo ? ACCENT : "gray.400"}
                            fontWeight="bold" fontSize="xs" transition="all 0.2s">
                            {feito ? <Icon as={Check} boxSize={3} /> : numero}
                        </Flex>
                        <Text fontSize="9px" fontWeight="semibold" letterSpacing="wide"
                            textTransform="uppercase" color={ativo ? ACCENT : "gray.400"}>
                            {nome}
                        </Text>
                    </Flex>
                    {idx < PASSOS.length - 1 && <Box w={{ base: "14px", sm: "34px" }} h="2px" bg="gray.200" mb={3} />}
                </Flex>
            );
        })}
    </Flex>
);

const CardPosicao = ({ posicao, bloqueada, onSelecionar, onRevisar }) => {
    const faixa = faixaDe(posicao);
    return (
        <Box bg="white" borderRadius="xl" boxShadow="0 1px 2px rgba(0,0,0,0.04)"
            border="1px solid" borderColor="gray.200"
            borderLeft="3px solid" borderLeftColor={faixa.cor}
            p={3} opacity={bloqueada ? 0.7 : 1}
            cursor={bloqueada ? "default" : "pointer"}
            _hover={bloqueada ? {} : { boxShadow: "0 8px 20px rgba(0,0,0,0.08)", transform: "translateY(-2px)", borderColor: "gray.300" }}
            transition="all 0.18s ease"
            onClick={() => !bloqueada && onSelecionar(posicao)}>
            <Flex align="center" justify="space-between" gap={1.5} mb={1} minW={0}>
                <Flex align="center" gap={1.5} minW={0}>
                    <Icon as={MapPin} boxSize={3} color={faixa.cor} flexShrink={0} />
                    <Text fontWeight="bold" fontSize="md" color="gray.800" letterSpacing="tight" noOfLines={1}>
                        {posicao.localizacao}
                    </Text>
                </Flex>
                <Tooltip label={textoSaude(posicao)} placement="top" hasArrow>
                    <Badge px={1.5} py={0.5} borderRadius="md" flexShrink={0}
                        bg={faixa.bg} color={faixa.texto} fontWeight="bold" fontSize="10px" textTransform="none">
                        {posicao.saude === null ? "nunca" : `${posicao.saude}%`}
                    </Badge>
                </Tooltip>
            </Flex>
            <Flex gap={3} mt={1}>
                <Box>
                    <Text fontSize="10px" color="gray.400" textTransform="uppercase" letterSpacing="wide" fontWeight="semibold">Itens</Text>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">{posicao.total_itens}</Text>
                </Box>
                <Box>
                    <Text fontSize="10px" color="gray.400" textTransform="uppercase" letterSpacing="wide" fontWeight="semibold">Unid.</Text>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">{posicao.total_quantidade}</Text>
                </Box>
            </Flex>
            <Flex align="center" gap={1} mt={1.5}>
                <Icon as={Clock} boxSize={2.5} color="gray.400" flexShrink={0} />
                <Text fontSize="10px" color="gray.500" noOfLines={1}>
                    {posicao.ultima_revisao ? format(parseISO(posicao.ultima_revisao), "dd/MM/yy", { locale: ptBR }) : "sem revisão"}
                </Text>
            </Flex>
            {bloqueada && onRevisar && (
                <Button size="xs" mt={2} w="full" variant="outline" borderColor="gray.300" color="gray.600"
                    leftIcon={<ShieldCheck size={12} />} fontWeight="semibold" fontSize="11px"
                    _hover={{ bg: "gray.50", borderColor: ACCENT, color: ACCENT }}
                    onClick={(e) => { e.stopPropagation(); onRevisar(posicao.localizacao); }}>
                    Revisar posição
                </Button>
            )}
        </Box>
    );
};

const TransferenciaEstoque = () => {
    const toast = useToast();
    const navigate = useNavigate();

    const [aba, setAba] = useState("nova");
    const [passo, setPasso] = useState(1);

    const [posicoes, setPosicoes] = useState([]);
    const [resumo, setResumo] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [busca, setBusca] = useState("");
    const [ordem, setOrdem] = useState("saude_desc");
    const [faixa, setFaixa] = useState("");
    const [soLiberadas, setSoLiberadas] = useState(true);

    const [origem, setOrigem] = useState(null);
    const [destino, setDestino] = useState(null);
    const [itens, setItens] = useState([]);
    const [carregandoItens, setCarregandoItens] = useState(false);
    const [buscaItem, setBuscaItem] = useState("");
    const [quantidades, setQuantidades] = useState({});
    const [observacao, setObservacao] = useState("");
    const [salvando, setSalvando] = useState(false);

    const [modoDestino, setModoDestino] = useState("existente");
    const [codigoNovo, setCodigoNovo] = useState("");

    const [scannerAberto, setScannerAberto] = useState(false);
    const [alvoScanner, setAlvoScanner] = useState("origem");

    const [historico, setHistorico] = useState([]);
    const [carregandoHistorico, setCarregandoHistorico] = useState(false);

    const confirmRef = useRef();
    const { isOpen: confirmAberto, onOpen: abrirConfirm, onClose: fecharConfirm } = useDisclosure();

    const carregarPosicoes = useCallback(async () => {
        setCarregando(true);
        try {
            const li = ordem.lastIndexOf("_");
            const res = await getTransferenciaEstoquePosicoes({
                order_by: ordem.slice(0, li),
                order_dir: ordem.slice(li + 1),
                ...(faixa ? { faixa } : {}),
            });
            setPosicoes(res.data.data || []);
            setResumo(res.data.resumo || null);
        } catch (e) {
            console.error(e);
            toast({ title: "Erro ao carregar posições", status: "error", duration: 3000 });
        } finally {
            setCarregando(false);
        }
    }, [ordem, faixa, toast]);

    const carregarHistorico = useCallback(async () => {
        setCarregandoHistorico(true);
        try {
            const res = await getTransferenciaEstoqueHistorico();
            setHistorico(res.data.data || []);
        } catch (e) {
            console.error(e);
            toast({ title: "Erro ao carregar histórico", status: "error", duration: 3000 });
        } finally {
            setCarregandoHistorico(false);
        }
    }, [toast]);

    useEffect(() => {
        if (aba === "nova" && (passo === 1 || passo === 3)) carregarPosicoes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aba, passo, ordem, faixa]);

    useEffect(() => {
        if (aba === "historico") carregarHistorico();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aba]);

    const selecionarOrigem = async (posicao) => {
        setOrigem(posicao);
        setCarregandoItens(true);
        setPasso(2);
        try {
            const res = await getTransferenciaEstoqueItens(posicao.localizacao);
            setItens(res.data.data || []);
            setQuantidades({});
        } catch (e) {
            console.error(e);
            toast({ title: "Erro ao carregar os itens da posição", status: "error", duration: 3000 });
            setPasso(1);
        } finally {
            setCarregandoItens(false);
        }
    };

    const posicoesVisiveis = useMemo(() => {
        const q = busca.trim().toLowerCase();
        let lista = posicoes;
        if (passo === 1 && soLiberadas) lista = lista.filter((p) => p.pode_ser_origem);
        if (passo === 3 && origem) lista = lista.filter((p) => p.localizacao !== origem.localizacao);
        if (q) lista = lista.filter((p) => (p.localizacao || "").toLowerCase().includes(q));
        return lista;
    }, [posicoes, busca, passo, soLiberadas, origem]);

    const destinoDigitado = useMemo(() => {
        const codigo = codigoNovo.trim();
        if (!codigo) return null;
        const chave = chavePosicao(codigo);
        if (origem && chave === chavePosicao(origem.localizacao)) return { tipo: "origem", codigo };
        const existente = posicoes.find((p) => chavePosicao(p.localizacao) === chave);
        if (existente) return { tipo: "existente", codigo, posicao: existente };
        return { tipo: "nova", codigo };
    }, [codigoNovo, posicoes, origem]);

    const abrirNovaPosicao = (codigo = "") => {
        setModoDestino("nova");
        setCodigoNovo(codigo);
        if (faixa) setFaixa("");
    };

    const usarDestinoDigitado = () => {
        if (!destinoDigitado || destinoDigitado.tipo === "origem") return;
        setDestino(destinoDigitado.tipo === "existente"
            ? destinoDigitado.posicao
            : { localizacao: destinoDigitado.codigo, nova: true, saude: null, faixa: "nunca" });
        setPasso(4);
    };

    const itensVisiveis = useMemo(() => {
        const q = buscaItem.trim().toLowerCase();
        if (!q) return itens;
        return itens.filter((i) =>
            [i.nome_produto, i.codigo_global, i.codigo_fornecedor, i.codigo_barra, i.codigo_fabricante]
                .some((c) => (c || "").toLowerCase().includes(q))
        );
    }, [itens, buscaItem]);

    const selecionados = useMemo(
        () => Object.entries(quantidades).filter(([, q]) => Number(q) > 0),
        [quantidades]
    );

    const totalUnidades = useMemo(
        () => selecionados.reduce((acc, [, q]) => acc + Number(q), 0),
        [selecionados]
    );

    const transferindoTudo = useMemo(() => {
        if (!itens.length || selecionados.length !== itens.length) return false;
        return itens.every((i) => Number(quantidades[i.produto_id] || 0) === Number(i.quantidade_sistema));
    }, [itens, quantidades, selecionados]);

    const ajustar = (item, valor) => {
        const max = Number(item.quantidade_sistema) || 0;
        const n = Math.max(0, Math.min(max, Number(valor) || 0));
        setQuantidades((atual) => ({ ...atual, [item.produto_id]: n }));
    };

    const marcarTudo = () => {
        if (transferindoTudo) {
            setQuantidades({});
            return;
        }
        const todos = {};
        itens.forEach((i) => { todos[i.produto_id] = Number(i.quantidade_sistema) || 0; });
        setQuantidades(todos);
    };

    const onScan = (codigo) => {
        setScannerAberto(false);
        const texto = String(codigo || "").trim();
        if (!texto) return;

        if (alvoScanner === "item") {
            setBuscaItem(texto);
            return;
        }

        const achada = posicoes.find((p) => chavePosicao(p.localizacao) === chavePosicao(texto));
        if (!achada) {
            if (alvoScanner === "destino") {
                abrirNovaPosicao(texto);
                return;
            }
            toast({ title: `Posição ${texto} não encontrada`, status: "warning", duration: 3000 });
            return;
        }

        if (alvoScanner === "origem") {
            if (!achada.pode_ser_origem) {
                toast({
                    title: `${achada.localizacao} não está com 100% de saúde`,
                    description: "Revise a posição antes de transferir.",
                    status: "warning",
                    duration: 4000,
                });
                return;
            }
            selecionarOrigem(achada);
            return;
        }

        if (achada.localizacao === origem?.localizacao) {
            toast({ title: "O destino precisa ser diferente da origem", status: "warning", duration: 3000 });
            return;
        }
        setDestino(achada);
        setPasso(4);
    };

    const confirmar = async () => {
        fecharConfirm();
        setSalvando(true);
        try {
            await salvarTransferenciaEstoque({
                origem: origem.localizacao,
                destino: destino.localizacao,
                modo: transferindoTudo ? "completa" : "parcial",
                observacao: observacao || null,
                itens: selecionados.map(([produtoId, qtd]) => ({
                    produto_id: Number(produtoId),
                    quantidade: Number(qtd),
                })),
            });
            toast({
                title: "Transferência aplicada",
                description: `${totalUnidades} unidades de ${origem.localizacao} para ${destino.localizacao}${destino.nova ? " (posição nova)" : ""}`,
                status: "success",
                duration: 4000,
            });
            setOrigem(null);
            setDestino(null);
            setModoDestino("existente");
            setCodigoNovo("");
            setItens([]);
            setQuantidades({});
            setObservacao("");
            setBusca("");
            setBuscaItem("");
            setPasso(1);
        } catch (e) {
            toast({
                title: "Não foi possível transferir",
                description: e?.response?.data?.error || "Tente novamente.",
                status: "error",
                duration: 7000,
            });
        } finally {
            setSalvando(false);
        }
    };

    const voltar = () => {
        if (passo === 2) { setOrigem(null); setItens([]); setQuantidades({}); }
        if (passo === 3) setDestino(null);
        if (passo === 4) setDestino(null);
        setPasso(passo === 4 ? 3 : passo - 1);
    };

    const abrirScanner = (alvo) => { setAlvoScanner(alvo); setScannerAberto(true); };

    return (
        <Box ml={{ base: 0, md: "64px" }} pt="56px" minH="100vh" bg="gray.50">
            <ScrollToTop />
            <Box maxW="1100px" mx="auto" px={{ base: 3, md: 6 }} pt={{ base: 2, md: 3 }} pb={{ base: 4, md: 6 }}>

            <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} mb={4} gap={3}
                direction={{ base: "column", md: "row" }}
                borderBottom="2px solid" borderColor="gray.200" pb={2}>
                <Box minW={0}>
                    <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="gray.800" lineHeight={1.15}>
                        Transferência de Estoque
                    </Text>
                    <Text fontSize="11px" color="gray.500" mt="1px">Mover itens de uma posição para outra</Text>
                </Box>
                <Flex gap={1}>
                    <Button size="sm" variant={aba === "nova" ? "solid" : "ghost"}
                        bg={aba === "nova" ? ACCENT : "transparent"}
                        color={aba === "nova" ? "white" : "gray.600"}
                        _hover={{ bg: aba === "nova" ? ACCENT : "gray.100" }}
                        leftIcon={<ArrowRightLeft size={14} />}
                        onClick={() => setAba("nova")}>
                        Nova
                    </Button>
                    <Button size="sm" variant={aba === "historico" ? "solid" : "ghost"}
                        bg={aba === "historico" ? ACCENT : "transparent"}
                        color={aba === "historico" ? "white" : "gray.600"}
                        _hover={{ bg: aba === "historico" ? ACCENT : "gray.100" }}
                        leftIcon={<History size={14} />}
                        onClick={() => setAba("historico")}>
                        Histórico
                    </Button>
                </Flex>
            </Flex>

            {aba === "nova" && (
                <Box>
                    <Stepper atual={passo} />

                    {(passo === 2 || passo === 3 || passo === 4) && (
                        <Flex align="center" gap={2} mb={4} wrap="wrap">
                            <IconButton icon={<ChevronLeft size={18} />} size="sm" variant="ghost"
                                onClick={voltar} aria-label="Voltar" borderRadius="lg" />
                            <Badge px={2} py={1} borderRadius="md" bg="gray.100" color="gray.700" fontSize="xs" textTransform="none"
                                whiteSpace="normal" overflowWrap="anywhere" maxW="full">
                                Origem: <b>{origem?.localizacao}</b>
                            </Badge>
                            {destino && (
                                <>
                                    <Icon as={ArrowRightLeft} boxSize={3} color="gray.400" />
                                    <Badge px={2} py={1} borderRadius="md" bg="orange.50" color="orange.700" fontSize="xs" textTransform="none"
                                        whiteSpace="normal" overflowWrap="anywhere" maxW="full">
                                        Destino: <b>{destino.localizacao}</b>
                                    </Badge>
                                </>
                            )}
                        </Flex>
                    )}

                    {passo === 3 && (
                        <ModoDestino valor={modoDestino}
                            onMudar={(modo) => (modo === "nova" ? abrirNovaPosicao(codigoNovo || busca.trim()) : setModoDestino("existente"))} />
                    )}

                    {passo === 3 && modoDestino === "nova" && (
                        <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200"
                            boxShadow="0 1px 2px rgba(0,0,0,0.04)" p={{ base: 4, md: 5 }} maxW={{ md: "560px" }}>
                            <Text as="label" htmlFor="codigo-nova-posicao" fontSize="sm" fontWeight="semibold" color="gray.700">
                                Código da posição
                            </Text>
                            <Flex gap={2} mt={2}>
                                <Input id="codigo-nova-posicao" value={codigoNovo} autoFocus
                                    onChange={(e) => setCodigoNovo(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") usarDestinoDigitado(); }}
                                    placeholder="Ex.: B-2-5" size="lg" bg="white" borderRadius="lg" fontWeight="semibold"
                                    maxLength={100} autoComplete="off" autoCapitalize="characters" spellCheck={false}
                                    isInvalid={destinoDigitado?.tipo === "origem"}
                                    _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                                <IconButton icon={<ScanLine size={18} />} aria-label="Escanear código da posição"
                                    size="lg" variant="outline" borderRadius="lg" borderColor="gray.300" flexShrink={0}
                                    onClick={() => abrirScanner("destino")} />
                            </Flex>
                            {destinoDigitado && (
                                <Flex mt={3} gap={2} align="flex-start" p={3} borderRadius="lg"
                                    bg={AVISO_DESTINO[destinoDigitado.tipo].bg} color={AVISO_DESTINO[destinoDigitado.tipo].cor}
                                    role="status">
                                    <Icon as={AVISO_DESTINO[destinoDigitado.tipo].icone} boxSize={4} mt="2px" flexShrink={0} />
                                    <Text fontSize="sm" minW={0} overflowWrap="anywhere">
                                        {destinoDigitado.tipo === "nova" && <><b>{destinoDigitado.codigo}</b> ainda não existe e será criada com os itens transferidos.</>}
                                        {destinoDigitado.tipo === "existente" && <><b>{destinoDigitado.posicao.localizacao}</b> já existe. Os itens serão somados a ela.</>}
                                        {destinoDigitado.tipo === "origem" && <>Essa é a posição de origem. Escolha outra.</>}
                                    </Text>
                                </Flex>
                            )}
                            <Button mt={4} w="full" size="lg" bg={ACCENT} color="white" borderRadius="lg"
                                _hover={{ bg: "#d97023" }} rightIcon={<ArrowRightLeft size={16} />}
                                isDisabled={!destinoDigitado || destinoDigitado.tipo === "origem"}
                                onClick={usarDestinoDigitado}>
                                Continuar
                            </Button>
                        </Box>
                    )}

                    {(passo === 1 || (passo === 3 && modoDestino === "existente")) && (
                        <Box>
                            {passo === 1 && resumo && (
                                <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200"
                                    boxShadow="0 1px 2px rgba(0,0,0,0.04)" p={3} mb={4}>
                                    <Flex align="center" justify="space-between" gap={3} wrap="wrap">
                                        <Box>
                                            <Text fontSize="10px" color="gray.400" textTransform="uppercase" letterSpacing="wide" fontWeight="semibold">
                                                Liberadas para transferir
                                            </Text>
                                            <Flex align="baseline" gap={1.5}>
                                                <Text fontSize="2xl" fontWeight="bold" lineHeight="1"
                                                    color={resumo.liberadas_transferencia > 0 ? "#16A34A" : "#DC2626"}>
                                                    {resumo.liberadas_transferencia}
                                                </Text>
                                                <Text fontSize="xs" color="gray.400">de {resumo.total_posicoes} posições</Text>
                                            </Flex>
                                        </Box>
                                        <Text fontSize="xs" color="gray.500" maxW="380px">
                                            Só posições com <b>100% de saúde</b> (revisadas hoje) podem ser origem.
                                        </Text>
                                    </Flex>
                                </Box>
                            )}

                            <Text fontWeight="semibold" color="gray.700" mb={3} fontSize="sm">
                                {passo === 1 ? "Selecione a posição de origem" : "Selecione a posição de destino"}
                            </Text>

                            <Flex gap={2} mb={4} direction={{ base: "column", sm: "row" }}>
                                <InputGroup size="md" flex="1">
                                    <InputLeftElement><Search size={16} color="#9CA3AF" /></InputLeftElement>
                                    <Input placeholder="Filtrar posições..." value={busca}
                                        onChange={(e) => setBusca(e.target.value)}
                                        bg="white" borderRadius="lg" pr={busca ? 10 : 4}
                                        _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                                    {busca && (
                                        <InputRightElement>
                                            <IconButton icon={<X size={14} />} size="xs" variant="ghost" color="gray.400"
                                                onClick={() => setBusca("")} aria-label="Limpar" borderRadius="full" />
                                        </InputRightElement>
                                    )}
                                </InputGroup>
                                <Button size="md" variant="outline" borderRadius="lg" borderColor="gray.300"
                                    leftIcon={<ScanLine size={16} />} flexShrink={0}
                                    onClick={() => abrirScanner(passo === 1 ? "origem" : "destino")}>
                                    Escanear
                                </Button>
                                {passo === 3 && (
                                    <Select w={{ base: "full", sm: "190px" }} size="md" bg="white" borderRadius="lg"
                                        value={faixa} onChange={(e) => setFaixa(e.target.value)} icon={<Filter size={14} />}
                                        _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }}>
                                        <option value="">Todas as saúdes</option>
                                        <option value="boa">Boa</option>
                                        <option value="atencao">Atenção</option>
                                        <option value="critica">Crítica</option>
                                        <option value="nunca">Nunca revisada</option>
                                    </Select>
                                )}
                                <Select w={{ base: "full", sm: "210px" }} size="md" bg="white" borderRadius="lg"
                                    value={ordem} onChange={(e) => setOrdem(e.target.value)} icon={<ArrowUpDown size={14} />}
                                    _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }}>
                                    <option value="saude_desc">Saúde (maior primeiro)</option>
                                    <option value="saude_asc">Saúde (menor primeiro)</option>
                                    <option value="localizacao_asc">Posição (A-Z)</option>
                                    <option value="localizacao_desc">Posição (Z-A)</option>
                                    <option value="total_quantidade_desc">Mais unidades</option>
                                </Select>
                            </Flex>

                            {passo === 1 && (
                                <Flex align="center" gap={2} mb={4}>
                                    <Button size="xs" variant={soLiberadas ? "solid" : "outline"}
                                        bg={soLiberadas ? ACCENT : "transparent"}
                                        color={soLiberadas ? "white" : "gray.600"}
                                        borderColor="gray.300"
                                        _hover={{ bg: soLiberadas ? ACCENT : "gray.50" }}
                                        onClick={() => setSoLiberadas(!soLiberadas)}>
                                        {soLiberadas ? "Mostrando só as liberadas" : "Mostrando todas"}
                                    </Button>
                                </Flex>
                            )}

                            {carregando ? (
                                <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={3}>
                                    {[...Array(8)].map((_, i) => (
                                        <Skeleton key={i} h="100px" borderRadius="xl" startColor="gray.100" endColor="gray.200" />
                                    ))}
                                </SimpleGrid>
                            ) : posicoesVisiveis.length === 0 ? (
                                <Flex direction="column" align="center" py={12} gap={3}>
                                    <Flex w="60px" h="60px" borderRadius="full" bg="gray.100" align="center" justify="center">
                                        <Icon as={Package} boxSize={7} color="gray.400" />
                                    </Flex>
                                    <Text color="gray.500" fontSize="sm" textAlign="center">
                                        {passo === 1 && soLiberadas
                                            ? "Nenhuma posição está com 100% de saúde. Revise uma posição para liberá-la."
                                            : "Nenhuma posição encontrada"}
                                    </Text>
                                    {passo === 1 && soLiberadas && (
                                        <Button size="sm" variant="outline" borderColor="gray.300"
                                            onClick={() => setSoLiberadas(false)}>
                                            Ver todas as posições
                                        </Button>
                                    )}
                                    {passo === 3 && busca.trim() && (
                                        <Button size="sm" bg={ACCENT} color="white" _hover={{ bg: "#d97023" }}
                                            leftIcon={<MapPinPlus size={14} />} maxW="full" whiteSpace="normal" h="auto" py={2}
                                            onClick={() => abrirNovaPosicao(busca.trim())}>
                                            Criar a posição {busca.trim()}
                                        </Button>
                                    )}
                                </Flex>
                            ) : (
                                <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={3}>
                                    {posicoesVisiveis.map((p) => (
                                        <CardPosicao key={p.localizacao} posicao={p}
                                            bloqueada={passo === 1 && !p.pode_ser_origem}
                                            onSelecionar={passo === 1
                                                ? selecionarOrigem
                                                : (pos) => { setDestino(pos); setPasso(4); }}
                                            onRevisar={passo === 1
                                                ? (loc) => navigate(`/revisao-estoque?posicao=${encodeURIComponent(loc)}`)
                                                : null} />
                                    ))}
                                </SimpleGrid>
                            )}
                        </Box>
                    )}

                    {passo === 2 && (
                        <Box>
                            <Flex gap={2} mb={3} direction={{ base: "column", sm: "row" }}>
                                <InputGroup size="md" flex="1">
                                    <InputLeftElement><Search size={16} color="#9CA3AF" /></InputLeftElement>
                                    <Input placeholder="Filtrar itens..." value={buscaItem}
                                        onChange={(e) => setBuscaItem(e.target.value)}
                                        bg="white" borderRadius="lg" pr={buscaItem ? 10 : 4}
                                        _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                                    {buscaItem && (
                                        <InputRightElement>
                                            <IconButton icon={<X size={14} />} size="xs" variant="ghost" color="gray.400"
                                                onClick={() => setBuscaItem("")} aria-label="Limpar" borderRadius="full" />
                                        </InputRightElement>
                                    )}
                                </InputGroup>
                                <Button size="md" variant="outline" borderRadius="lg" borderColor="gray.300"
                                    leftIcon={<ScanLine size={16} />} flexShrink={0}
                                    onClick={() => abrirScanner("item")}>
                                    Escanear
                                </Button>
                                <Button size="md" borderRadius="lg" flexShrink={0}
                                    variant={transferindoTudo ? "solid" : "outline"}
                                    bg={transferindoTudo ? ACCENT : "transparent"}
                                    color={transferindoTudo ? "white" : "gray.600"}
                                    borderColor="gray.300"
                                    _hover={{ bg: transferindoTudo ? ACCENT : "gray.50" }}
                                    onClick={marcarTudo}>
                                    {transferindoTudo ? "Transferindo tudo" : "Transferir tudo"}
                                </Button>
                            </Flex>

                            {carregandoItens ? (
                                <Box>{[...Array(5)].map((_, i) => (
                                    <Skeleton key={i} h="66px" borderRadius="xl" mb={2} startColor="gray.100" endColor="gray.200" />
                                ))}</Box>
                            ) : itensVisiveis.length === 0 ? (
                                <Flex direction="column" align="center" py={12} gap={3}>
                                    <Icon as={Package} boxSize={7} color="gray.400" />
                                    <Text color="gray.500" fontSize="sm">Nenhum item nesta posição</Text>
                                </Flex>
                            ) : (
                                <Box>
                                    {itensVisiveis.map((item) => {
                                        const qtd = Number(quantidades[item.produto_id] || 0);
                                        const max = Number(item.quantidade_sistema) || 0;
                                        return (
                                            <Flex key={item.produto_id} bg="white" borderRadius="xl" p={2.5} mb={2}
                                                border="1px solid" borderColor={qtd > 0 ? ACCENT : "gray.200"}
                                                boxShadow="0 1px 2px rgba(0,0,0,0.04)" align="center" gap={2.5}
                                                transition="border-color 0.15s">
                                                <Box flexShrink={0}>
                                                    <ProdutoImagem
                                                        produto={item.produto_estoque}
                                                        origem={null}
                                                        width="44px"
                                                        height="44px"
                                                        imagemAPI={item.imagem_url || ""}
                                                    />
                                                </Box>
                                                <Box flex="1" minW={0}>
                                                    <Text fontSize="xs" fontWeight="semibold" color="gray.800" noOfLines={2}>
                                                        {nomeDe(item)}
                                                    </Text>
                                                    <Flex gap={2} mt={0.5} wrap="wrap">
                                                        {item.codigo_fornecedor && (
                                                            <Text fontSize="10px" color="gray.400">{item.codigo_fornecedor}</Text>
                                                        )}
                                                        <Text fontSize="10px" color="gray.500" fontWeight="semibold">
                                                            {max} disponíveis
                                                        </Text>
                                                        {item.linhas > 1 && (
                                                            <Tooltip label={`Este item está em ${item.linhas} registros nesta posição (devoluções). A soma é o disponível.`} hasArrow>
                                                                <Text fontSize="10px" color="gray.400">({item.linhas} registros)</Text>
                                                            </Tooltip>
                                                        )}
                                                    </Flex>
                                                </Box>
                                                <Flex align="center" gap={1} flexShrink={0}>
                                                    <IconButton icon={<Minus size={13} />} size="xs" variant="outline"
                                                        borderColor="gray.300" borderRadius="md" aria-label="Menos"
                                                        isDisabled={qtd <= 0} onClick={() => ajustar(item, qtd - 1)} />
                                                    <Input value={qtd} onChange={(e) => ajustar(item, e.target.value)}
                                                        w="46px" size="sm" textAlign="center" borderRadius="md"
                                                        type="number" inputMode="numeric"
                                                        _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                                                    <IconButton icon={<Plus size={13} />} size="xs" variant="outline"
                                                        borderColor="gray.300" borderRadius="md" aria-label="Mais"
                                                        isDisabled={qtd >= max} onClick={() => ajustar(item, qtd + 1)} />
                                                </Flex>
                                            </Flex>
                                        );
                                    })}
                                </Box>
                            )}

                            <Flex justify="space-between" align="center" mt={4} gap={3} wrap="wrap">
                                <Text fontSize="sm" color="gray.600">
                                    <b>{selecionados.length}</b> itens, <b>{totalUnidades}</b> unidades
                                </Text>
                                <Button bg={ACCENT} color="white" _hover={{ bg: "#d97023" }}
                                    rightIcon={<ArrowRightLeft size={15} />} borderRadius="lg"
                                    isDisabled={selecionados.length === 0}
                                    onClick={() => setPasso(3)}>
                                    Escolher destino
                                </Button>
                            </Flex>
                        </Box>
                    )}

                    {passo === 4 && origem && destino && (
                        <Box>
                            <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200"
                                boxShadow="0 1px 2px rgba(0,0,0,0.04)" p={4} mb={4}>
                                <Flex align="center" justify="center" gap={3} mb={4} wrap="wrap">
                                    <Box textAlign="center">
                                        <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="semibold">Origem</Text>
                                        <Text fontSize="xl" fontWeight="bold" color="gray.800">{origem.localizacao}</Text>
                                        <Badge bg="green.50" color="green.700" fontSize="10px" textTransform="none">
                                            saúde {origem.saude}%
                                        </Badge>
                                    </Box>
                                    <Icon as={ArrowRightLeft} boxSize={5} color={ACCENT} />
                                    <Box textAlign="center">
                                        <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="semibold">Destino</Text>
                                        <Text fontSize="xl" fontWeight="bold" color="gray.800" overflowWrap="anywhere">{destino.localizacao}</Text>
                                        {destino.nova ? (
                                            <Badge bg="blue.50" color="blue.700" fontSize="10px" textTransform="none">
                                                posição nova
                                            </Badge>
                                        ) : (
                                            <Badge bg={faixaDe(destino).bg} color={faixaDe(destino).texto} fontSize="10px" textTransform="none">
                                                {destino.saude === null ? "nunca revisada" : `saúde ${destino.saude}%`}
                                            </Badge>
                                        )}
                                    </Box>
                                </Flex>

                                <Divider mb={3} />

                                {selecionados.map(([produtoId, qtd]) => {
                                    const item = itens.find((i) => String(i.produto_id) === String(produtoId));
                                    return (
                                        <Flex key={produtoId} justify="space-between" align="center" py={1.5} gap={2} minW={0}>
                                            <Text fontSize="xs" color="gray.700" noOfLines={1}>{nomeDe(item)}</Text>
                                            <Text fontSize="xs" fontWeight="bold" color="gray.800" whiteSpace="nowrap">
                                                {qtd} un
                                            </Text>
                                        </Flex>
                                    );
                                })}

                                <Divider my={3} />

                                <Flex justify="space-between" align="center">
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">Total</Text>
                                    <Text fontSize="sm" fontWeight="bold" color={ACCENT}>
                                        {selecionados.length} itens, {totalUnidades} unidades
                                    </Text>
                                </Flex>
                            </Box>

                            <Textarea placeholder="Observação (opcional)" value={observacao}
                                onChange={(e) => setObservacao(e.target.value)} bg="white" borderRadius="lg"
                                size="sm" rows={2} mb={4} maxLength={1000}
                                _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />

                            <Button w="full" bg={ACCENT} color="white" _hover={{ bg: "#d97023" }}
                                size="lg" borderRadius="lg" leftIcon={<Check size={18} />}
                                isLoading={salvando} loadingText="Transferindo"
                                onClick={abrirConfirm}>
                                Confirmar transferência
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {aba === "historico" && (
                <Box>
                    {carregandoHistorico ? (
                        <Box>{[...Array(6)].map((_, i) => (
                            <Skeleton key={i} h="70px" borderRadius="xl" mb={2} startColor="gray.100" endColor="gray.200" />
                        ))}</Box>
                    ) : historico.length === 0 ? (
                        <Flex direction="column" align="center" py={12} gap={3}>
                            <Icon as={History} boxSize={7} color="gray.400" />
                            <Text color="gray.500" fontSize="sm">Nenhuma transferência registrada</Text>
                        </Flex>
                    ) : (
                        historico.map((t) => (
                            <Box key={t.id} bg="white" borderRadius="xl" p={3} mb={2}
                                border="1px solid" borderColor="gray.200" boxShadow="0 1px 2px rgba(0,0,0,0.04)">
                                <Flex justify="space-between" align="center" gap={2} wrap="wrap">
                                    <Flex align="center" gap={2} minW={0} wrap="wrap">
                                        <Text fontWeight="bold" fontSize="sm" color="gray.800">{t.origem}</Text>
                                        <Icon as={ArrowRightLeft} boxSize={3} color={ACCENT} />
                                        <Text fontWeight="bold" fontSize="sm" color="gray.800">{t.destino}</Text>
                                        <Badge bg="gray.100" color="gray.600" fontSize="10px" textTransform="none">
                                            {t.modo === "completa" ? "completa" : "parcial"}
                                        </Badge>
                                    </Flex>
                                    <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                                        {t.aplicada_em ? format(parseISO(t.aplicada_em.replace(" ", "T")), "dd/MM/yy HH:mm", { locale: ptBR }) : ""}
                                    </Text>
                                </Flex>
                                <Flex gap={3} mt={1.5} wrap="wrap">
                                    <Text fontSize="11px" color="gray.500">{t.total_itens} itens</Text>
                                    <Text fontSize="11px" color="gray.500">{t.total_quantidade} unidades</Text>
                                    <Text fontSize="11px" color="gray.500">saúde da origem {t.saude_origem}%</Text>
                                    {t.usuario && <Text fontSize="11px" color="gray.400">{t.usuario}</Text>}
                                </Flex>
                                {t.observacao && (
                                    <Text fontSize="11px" color="gray.400" fontStyle="italic" mt={1}>{t.observacao}</Text>
                                )}
                            </Box>
                        ))
                    )}
                </Box>
            )}

            <BarcodeScanner isOpen={scannerAberto} onClose={() => setScannerAberto(false)} onDetected={onScan} />

            <AlertDialog isOpen={confirmAberto} leastDestructiveRef={confirmRef} onClose={fecharConfirm} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent mx={4} borderRadius="xl">
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">Confirmar transferência</AlertDialogHeader>
                        <AlertDialogBody fontSize="sm" color="gray.600">
                            Mover <b>{totalUnidades} unidades</b> de <b>{origem?.localizacao}</b> para <b>{destino?.localizacao}</b>{destino?.nova ? " (posição nova)" : ""}.
                            O estoque total dos produtos não muda, só a posição.
                        </AlertDialogBody>
                        <AlertDialogFooter gap={2}>
                            <Button ref={confirmRef} onClick={fecharConfirm} size="sm" variant="ghost">Cancelar</Button>
                            <Button bg={ACCENT} color="white" _hover={{ bg: "#d97023" }} size="sm"
                                onClick={confirmar} isLoading={salvando}>
                                Transferir
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
            </Box>
        </Box>
    );
};

export default TransferenciaEstoque;
